import React, { useState, useRef, useMemo } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import JoditEditor from 'jodit-react';
import axios from 'axios';
import { notify, getErrorMessage } from '../feedback/notify';

/** Editor em tela cheia para cadastrar uma nova versão de documento legal. */
export default function AdicionarDocumentoDialog({ open, onClose, endpoint, title, description, placeholder }) {
    const token = localStorage.getItem('token');

    const editor = useRef(null);
    const [content, setContent] = useState('');
    const [loading, setLoading] = useState(false);

    const config = useMemo(() => ({
        width: '100%',
        height: 'calc(100vh - 260px)',
        readonly: false,
        placeholder,
        toolbarButtonSize: 'middle',
        toolbar: [
            'bold',
            'italic',
            'underline',
            '|',
            'ul',
            'ol',
            '|',
            'link',
            'undo',
            'redo'
        ],
    }), [placeholder]);

    const handleClose = () => {
        if (loading) return;
        setContent('');
        onClose();
    };

    const handleSubmit = async () => {
        // Usa o conteúdo atual do editor (o onBlur pode não ter disparado ainda).
        const conteudoAtual = editor.current?.value ?? content;
        const textoPuro = String(conteudoAtual || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
        if (!textoPuro) {
            notify.warning('O conteúdo do documento não pode ficar vazio.');
            return;
        }
        try {
            setLoading(true);
            await axios.post(`${process.env.REACT_APP_API_URL}/${endpoint}`, {
                content: conteudoAtual
            }, {
                headers: {
                    Authorization: token
                }
            });
            notify.success('Documento publicado com sucesso.');
            setContent('');
            onClose();
        } catch (error) {
            console.error("Erro ao adicionar documento:", error);
            notify.error(getErrorMessage(error, 'Não foi possível publicar o documento.'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={Boolean(open)} onClose={handleClose} fullScreen>
            <DialogTitle sx={{ borderBottom: '1px solid', borderColor: 'divider', pb: 2, pr: 7 }}>
                {title}
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontWeight: 400 }}>
                    {description}
                </Typography>
                <IconButton aria-label="Fechar" onClick={handleClose} sx={{ position: 'absolute', right: 16, top: 16, color: 'text.secondary' }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ bgcolor: 'background.default', pt: '24px !important' }}>
                <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
                    <JoditEditor
                        ref={editor}
                        value={content}
                        config={config}
                        tabIndex={1}
                        onBlur={newContent => setContent(newContent)}
                        onChange={() => { }}
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button variant="outlined" onClick={handleClose} disabled={loading}>Cancelar</Button>
                <Button variant="contained" onClick={handleSubmit} loading={loading}>Publicar nova versão</Button>
            </DialogActions>
        </Dialog>
    );
}
